import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Lock, Mail, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { adminLogin } from '../lib/adminApi';
import SEO from '../components/SEO';

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Enter email and password'); return; }
    setSubmitting(true);
    try {
      await adminLogin(email.trim().toLowerCase(), password);
      nav('/admin', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-blue-hero relative overflow-hidden flex items-center justify-center py-16 px-4" data-testid="admin-login-page">
      <SEO title="Admin Login — Russian Education" description="Secure admin dashboard for Russian Education inquiries." noindex />
      <div className="absolute inset-0 opacity-20"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55z'/%3E%3C/svg%3E\")", backgroundSize: '160px' }} />

      <div className="relative w-full max-w-md">
        <div className="text-center text-white mb-6">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-brand shadow-lg shadow-brand/40 flex items-center justify-center">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <h1 className="mt-4 font-serif text-3xl md:text-4xl font-extrabold tracking-tight">Admin Portal</h1>
          <p className="mt-2 text-sm text-white/85">Russian Education inquiries dashboard</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl border border-white/20 p-7 md:p-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-brand font-bold">Email</label>
            <div className="relative mt-1.5">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@russianeducation.in"
                className="w-full h-11 pl-9 pr-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
                autoComplete="username"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-brand font-bold">Password</label>
            <div className="relative mt-1.5">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-11 pl-9 pr-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <div data-testid="admin-login-error" className="flex items-start gap-2 p-3 bg-brand/5 border border-brand/30 rounded-lg text-brand text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            data-testid="admin-login-submit"
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-brand hover:bg-brand-dark text-white font-bold rounded-lg btn-shift disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : (<span className="inline-flex items-center gap-2"><LogIn className="w-4 h-4" /> Sign in</span>)}
          </Button>

          <p className="text-[11px] text-muted-foreground text-center pt-2">
            Authorised personnel only. Session locks after 5 failed attempts.
          </p>
        </form>
      </div>
    </div>
  );
}
